import React, { useState, useEffect } from "react";
import { Container, Button, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaCheckCircle, FaFilm, FaHome, FaDownload } from "react-icons/fa";
import api from "../services/api";
import MovieTicket from "../components/ticket/MovieTicket";

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      if (!bookingId) {
        setLoading(false);
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const baseDelay = 500;
      let response = null;

      while (attempts < maxAttempts && !response) {
        try {
          console.log(
            `📋 Fetching booking confirmation attempt ${attempts + 1}/${maxAttempts}...`,
          );
          response = await api.get(`/bookings/${bookingId}`);

          if (response.data) {
            console.log("✅ Booking confirmation fetched");
            setBooking(response.data);
            setLoading(false);
            return;
          }
        } catch (error) {
          attempts++;
          if (attempts < maxAttempts) {
            const delay = baseDelay * Math.pow(2, attempts - 1);
            console.log(`⏳ Retry in ${delay}ms...`);
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      console.error("❌ Could not fetch booking after retries");
      setLoading(false);
    } catch (error) {
      console.error("Error fetching booking:", error);
      setLoading(false);
    }
  };

  const handleDownloadTicket = () => {
    window.print();
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(180deg, #e2f1fe 0px, #ffffff 420px)",
        }}
      >
        <Spinner animation="border" style={{ color: "#e50914" }} />
      </div>
    );
  }

  if (!booking) {
    return (
      <Container className="text-center py-5 mt-5">
        <h4 className="text-dark">Booking Confirmed!</h4>
        <p className="text-muted mb-4">
          Your booking has been processed. Check your email for confirmation
          details.
        </p>
        <Button as={Link} to="/movies" variant="primary">
          Back to Movies
        </Button>
      </Container>
    );
  }

  return (
    <div
      className="min-vh-100 py-4 mt-5"
      style={{
        background: "linear-gradient(180deg, #e2f1fe 0px, #ffffff 420px)",
      }}
    >
      <Container>
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center py-4 mb-4"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="mb-3"
          >
            <FaCheckCircle
              className="text-success"
              style={{ fontSize: "3rem" }}
            />
          </motion.div>
          <h2 className="text-dark mb-2">Booking Confirmed!</h2>
          <p className="text-muted mb-0">
            Your movie tickets have been booked successfully
          </p>
        </motion.div>

        {/* Digital Ticket */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-4"
        >
          <MovieTicket booking={booking} onDownload={handleDownloadTicket} />
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="text-center py-4"
        >
          <div className="d-flex flex-wrap justify-content-center gap-3">
            <Button
              onClick={handleDownloadTicket}
              variant="warning"
              className="px-4"
            >
              <FaDownload className="me-2" />
              Download Ticket
            </Button>
            <Button as={Link} to="/movies" variant="success" className="px-4">
              <FaFilm className="me-2" />
              Book More Movies
            </Button>
            <Button as={Link} to="/" variant="outline-dark" className="px-4">
              <FaHome className="me-2" />
              Back to Home
            </Button>
          </div>
        </motion.div>
      </Container>
    </div>
  );
};

export default BookingConfirmation;
